import * as React from "react"
import { Command as CommandPrimitive } from "cmdk"
import { Search } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"

const CommandBase = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      "flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
      className
    )}
    {...props}
  />
))
CommandBase.displayName = CommandPrimitive.displayName

const CommandDialogBase = ({
  children,
  ...props
}: React.ComponentProps<typeof Dialog>) => {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0">
        <DialogTitle className="sr-only">Command</DialogTitle>
        <CommandBase className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </CommandBase>
      </DialogContent>
    </Dialog>
  )
}

const CommandInputBase = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        "flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    />
  </div>
))
CommandInputBase.displayName = CommandPrimitive.Input.displayName

const CommandListBase = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className)}
    {...props}
  />
))
CommandListBase.displayName = CommandPrimitive.List.displayName

const CommandEmptyBase = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className={cn("py-6 text-center text-sm", className)}
    {...props}
  />
))
CommandEmptyBase.displayName = CommandPrimitive.Empty.displayName

const CommandGroupBase = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      "overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground",
      className
    )}
    {...props}
  />
))
CommandGroupBase.displayName = CommandPrimitive.Group.displayName

const CommandSeparatorBase = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 h-px bg-border", className)}
    {...props}
  />
))
CommandSeparatorBase.displayName = CommandPrimitive.Separator.displayName

const CommandItemBase = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      className
    )}
    {...props}
  />
))
CommandItemBase.displayName = CommandPrimitive.Item.displayName

const CommandShortcutBase = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        "ml-auto text-xs tracking-widest text-muted-foreground",
        className
      )}
      {...props}
    />
  )
}
CommandShortcutBase.displayName = "CommandShortcut"

// --- uiv2: conditional Intui swap (build-time resolved by Vite) ---
import {
  IntuiCommand,
  IntuiCommandDialog,
  IntuiCommandInput,
  IntuiCommandList,
  IntuiCommandEmpty,
  IntuiCommandGroup,
  IntuiCommandItem,
  IntuiCommandSeparator,
  IntuiCommandShortcut,
} from '@/components/intueri/IntuiCommand'

const _uiv2 = import.meta.env.VITE_UIV2 === 'true'

// Lazy proxy: wraps Intui access in a render function to avoid TDZ errors from circular ESM imports.
// The Intui import bindings are uninitialized at module scope due to the circular dependency;
// deferring access to render-time ensures both modules have finished evaluating.
function _lazy<T>(getIntui: () => T, base: T): T {
  if (!_uiv2) return base
  // Dynamic-dispatch proxy — `any` is required so the generic _lazy<T> can
  // forward refs/props to whichever Intui component getIntui() resolves at
  // render time. The outer `as unknown as T` cast re-narrows for callers.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return React.forwardRef<any, any>((props, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Comp = getIntui() as any
    return <Comp ref={ref} {...props} />
  }) as unknown as T
}

const Command = _lazy(() => IntuiCommand, CommandBase)
const CommandDialog = _lazy(() => IntuiCommandDialog, CommandDialogBase)
const CommandInput = _lazy(() => IntuiCommandInput, CommandInputBase)
const CommandList = _lazy(() => IntuiCommandList, CommandListBase)
const CommandEmpty = _lazy(() => IntuiCommandEmpty, CommandEmptyBase)
const CommandGroup = _lazy(() => IntuiCommandGroup, CommandGroupBase)
const CommandItem = _lazy(() => IntuiCommandItem, CommandItemBase)
const CommandShortcut = _lazy(() => IntuiCommandShortcut, CommandShortcutBase)
const CommandSeparator = _lazy(() => IntuiCommandSeparator, CommandSeparatorBase)

export {
  CommandBase,
  CommandDialogBase,
  CommandInputBase,
  CommandListBase,
  CommandEmptyBase,
  CommandGroupBase,
  CommandItemBase,
  CommandShortcutBase,
  CommandSeparatorBase,
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
}
